import React, { useMemo } from 'react';
import { AccountBalanceWallet, PointOfSale } from '@mui/icons-material';

const formatAmount = (value, currency) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return `${n.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${currency}`;
};

const PERIODS = [
    { key: 'dailyAmount', label: "Aujourd'hui" },
    { key: 'monthlyAmount', label: 'Ce mois' },
    { key: 'yearlyAmount', label: 'Cette année' },
];

const initials = (name) =>
    String(name || '?')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('') || '?';

/**
 * Solde encaissé par caissier : paiements complétés dont le caissier est responsable
 * (accountable), avec la ou les caisses qui lui sont assignées.
 */
const CashierEncashmentSummary = ({
    posList = [],
    encashmentsByCashier = {},
    cashierProfiles = {},
    currency,
    onCurrencyChange,
    loading = false,
}) => {
    const rows = useMemo(() => {
        const byCashier = {};
        posList.forEach((pos) => {
            const cashierId = pos.managerId || pos.manager_id;
            if (!cashierId) return;
            if (!byCashier[cashierId]) {
                const profile = cashierProfiles[cashierId] || {};
                byCashier[cashierId] = {
                    cashierId,
                    name: profile.name || pos.cashier || cashierId,
                    phone: profile.phone || pos.cashier || '',
                    caisses: [],
                };
            }
            byCashier[cashierId].caisses.push({ id: pos.id, name: pos.name || 'Terminal', tag: pos.tag });
        });
        return Object.values(byCashier)
            .map((row) => ({ ...row, ...(encashmentsByCashier[row.cashierId] || {}) }))
            .sort((a, b) => (Number(b.monthlyAmount) || 0) - (Number(a.monthlyAmount) || 0));
    }, [posList, encashmentsByCashier, cashierProfiles]);

    const totals = useMemo(() => {
        const sum = (key) => rows.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);
        return { dailyAmount: sum('dailyAmount'), monthlyAmount: sum('monthlyAmount'), yearlyAmount: sum('yearlyAmount') };
    }, [rows]);

    const amountOrPlaceholder = (value, className = 'h-4 w-20') =>
        loading && value === undefined
            ? <span className={`inline-block rounded bg-slate-200 animate-pulse ${className}`} />
            : formatAmount(value ?? 0, currency);

    return (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                        <AccountBalanceWallet className="text-emerald-600" />
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold text-slate-800">Encaissé par caissier</h2>
                        <p className="text-xs text-slate-500">
                            {rows.length} caissier(s) · paiements complétés sur leurs caisses
                        </p>
                    </div>
                </div>
                <div className="inline-flex self-start sm:self-auto rounded-xl border border-slate-200 bg-white p-0.5">
                    {['CDF', 'USD'].map((c) => (
                        <button
                            key={c}
                            type="button"
                            onClick={() => onCurrencyChange?.(c)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                currency === c ? 'bg-amber-500 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'
                            }`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </div>

            {rows.length === 0 ? (
                <div className="px-5 py-10 text-center text-sm text-slate-500">
                    Aucun caissier n'est assigné à une caisse pour le moment.
                </div>
            ) : (
                <div className="p-5 space-y-5">
                    {/* Totaux toutes caisses */}
                    <div className="grid grid-cols-3 gap-3">
                        {PERIODS.map((p) => (
                            <div key={p.key} className="rounded-xl bg-slate-50 border border-slate-200/80 px-4 py-3">
                                <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">
                                    Total {p.label.toLowerCase()}
                                </p>
                                <p className="mt-1 text-base sm:text-xl font-bold text-slate-900 tabular-nums">
                                    {loading && rows.every((r) => r[p.key] === undefined) ? '…' : formatAmount(totals[p.key], currency)}
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* Une carte par caissier : identité, caisse(s), balance encaissée */}
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {rows.map((row) => (
                            <div key={row.cashierId} className="rounded-2xl border border-slate-200/80 p-4 flex flex-col gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-amber-500/15 text-amber-700 font-semibold text-sm flex items-center justify-center shrink-0">
                                        {initials(row.name)}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold text-slate-800 truncate">{row.name}</p>
                                        {row.phone && row.phone !== row.name && (
                                            <p className="text-xs text-slate-500">{row.phone}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="flex flex-wrap gap-1.5">
                                    {row.caisses.map((caisse) => (
                                        <span
                                            key={caisse.id}
                                            className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700"
                                        >
                                            <PointOfSale sx={{ fontSize: 14 }} className="text-slate-500" />
                                            {caisse.name}
                                            {caisse.tag && !String(caisse.name).includes(caisse.tag) && (
                                                <span className="font-mono text-slate-500">· {caisse.tag}</span>
                                            )}
                                        </span>
                                    ))}
                                </div>

                                <div className="rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3">
                                    <p className="text-[11px] uppercase tracking-wide font-semibold text-emerald-700">Encaissé ce mois</p>
                                    <p className="mt-1 text-2xl font-bold text-slate-900 tabular-nums">
                                        {amountOrPlaceholder(row.monthlyAmount, 'h-7 w-32')}
                                    </p>
                                </div>

                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <p className="text-xs text-slate-500">Aujourd'hui</p>
                                        <p className="font-semibold text-slate-800 tabular-nums">{amountOrPlaceholder(row.dailyAmount)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-500">Cette année</p>
                                        <p className="font-semibold text-slate-800 tabular-nums">{amountOrPlaceholder(row.yearlyAmount)}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CashierEncashmentSummary;
